import { ConnectProto } from "@racoon/api";
import { Namespace, type TFn, type TranslationKey } from "@racoon/i18n";
import { EMPTY_CELL, getEnumName, isBlank, isUndefinedEnum } from "@racoon/lib";
import type { GroupedRowMarkers } from "@racoon/ui";
import { isAroundTheClock } from "~/shared/lib/availability-schedule";
import { contractFromLabel } from "~/features/pickup-location-modal/model/contract-label";
import { scheduleLive, schedulesByCgId, todayKey } from "~/shared/lib/container-group-schedule";

/**
 * What a container-group row knows about its route groups and schedule rows,
 * resolved once per fetch. `ContainerGroup` itself does not carry the relation — it lives on
 * `PickupLocationGroup` — so the page joins it client-side from the groups and
 * trucks lists.
 */
type CgRouteInfo = {
  /** Every route group the container group is bound to, by name; `groupNames[i]` names `groupIds[i]`. */
  groupIds: string[];
  groupNames: string[];
  /** Those groups' default trucks — the "responsible crew" chips. */
  truckIds: string[];
  truckPlates: string[];
  /** The group has a row in the schedule table; undefined: unknown. */
  hasSchedule?: boolean;
};

export type CgRouteIndex = {
  byCg: Map<string, CgRouteInfo>;
  /** The schedules are not known: a group the map lacks is unknown, not unscheduled. */
  schedulesUnknown: boolean;
};

const emptyInfo = (): CgRouteInfo => ({
  groupIds: [],
  groupNames: [],
  truckIds: [],
  truckPlates: [],
  hasSchedule: false,
});

export const buildCgRouteIndex = (
  groups: ConnectProto.PickupLocationGroup[],
  trucks: ConnectProto.Truck[],
  /** Undefined: the schedules are unknown. */
  schedules?: ConnectProto.ContainerGroupSchedule[],
  /** Container groups whose schedules are not known yet. */
  unknownCgIds?: Set<string>,
): CgRouteIndex => {
  const plateByTruckId = new Map(trucks.map((truck) => [String(truck.id), truck.plateNumber]));

  const index = new Map<string, CgRouteInfo>();
  const byName = [...groups].sort((a, b) => (a.name < b.name ? -1 : a.name > b.name ? 1 : 0));
  for (const group of byName) {
    const truckId = group.defaultTruckId ? String(group.defaultTruckId) : undefined;
    const plate = truckId ? plateByTruckId.get(truckId) : undefined;
    for (const cg of group.containerGroups ?? []) {
      const key = String(cg.id);
      const info = index.get(key) ?? emptyInfo();
      info.groupIds.push(String(group.id));
      info.groupNames.push(group.name);
      if (truckId && !info.truckIds.includes(truckId)) info.truckIds.push(truckId);
      if (plate && !info.truckPlates.includes(plate)) info.truckPlates.push(plate);
      index.set(key, info);
    }
  }
  if (!schedules) {
    index.forEach((info) => (info.hasSchedule = undefined));
    return { byCg: index, schedulesUnknown: true };
  }
  // Only rows still in effect count, as in the reports.
  const today = todayKey();
  for (const key of schedulesByCgId(schedules.filter((s) => scheduleLive(s, today))).keys()) {
    index.set(key, { ...(index.get(key) ?? emptyInfo()), hasSchedule: true });
  }
  unknownCgIds?.forEach((key) =>
    index.set(key, { ...(index.get(key) ?? emptyInfo()), hasSchedule: undefined }),
  );
  return { byCg: index, schedulesUnknown: false };
};

/**
 * One row of the cascade: a pickup location's synthetic header, or one of its
 * container groups. `DataTable` sees a single flat list, which is what keeps
 * one column header above every group (see `ShiftsCascade` for the pattern).
 */
export type PickupLocationRow = GroupedRowMarkers & {
  /** The pickup location this row belongs to (the group itself on headers). */
  location: ConnectProto.PickupLocation;
  /** Present only on container-group rows. */
  cg?: ConnectProto.ContainerGroup;
  route?: CgRouteInfo;
  /** 1-based row number, set only on export rows (`accessor` gets no index). */
  exportIndex?: number;
};

/** The key of a location's group: its header row and the group its rows belong to. */
export const locationGroupKey = (location: ConnectProto.PickupLocation): string =>
  `pl-${location.id}`;

export const locationRowKey = (row: PickupLocationRow): string =>
  row._isGroupHeader || !row.cg ? locationGroupKey(row.location) : `cg-${row.cg.id}`;

/**
 * How every field of the cascade reads, in one place, so the table on screen
 * and the Excel export cannot drift apart — they render the same strings.
 */
export const locationCode = (location: ConnectProto.PickupLocation): string => `PL${location.id}`;

/** The provider's own type of the location, preloaded by the list; none reads as empty. */
export const locationTypeLabel = (location: ConnectProto.PickupLocation): string =>
  location.pickupLocationType?.name || EMPTY_CELL;

export const locationPriorityKey = (
  location: ConnectProto.PickupLocation,
): TranslationKey<Namespace.Enum> =>
  (getEnumName(ConnectProto.PickupLocationPrioritySchema, Number(location.priority ?? 0)) ??
    "PICKUP_LOCATION_PRIORITY_UNDEFINED") as TranslationKey<Namespace.Enum>;

/** The priority as text; an UNDEFINED one (no translation exists) is never shown. */
export const locationPriorityLabel = (location: ConnectProto.PickupLocation, t: TFn): string => {
  const key = locationPriorityKey(location);
  return isUndefinedEnum(key) ? EMPTY_CELL : t(Namespace.Enum, key);
};

/** A blank or whitespace-only name reads as "unnamed", on screen and in the export alike. */
export const locationNameLabel = (location: ConnectProto.PickupLocation, t: TFn): string =>
  isBlank(location.name) ? t(Namespace.Common, "unnamed") : location.name;

export const locationWorktime = (location: ConnectProto.PickupLocation, t: TFn): string => {
  if (isAroundTheClock(location.availabilitySchedule)) {
    return t(Namespace.Table, "aroundTheClock");
  }
  return location.availabilitySchedule || EMPTY_CELL;
};

export const containerGroupLabel = (row: PickupLocationRow): string =>
  row.cg
    ? `${row.cg.containerType?.name || EMPTY_CELL} (${row.cg.numberOfContainers})`
    : EMPTY_CELL;

export const routeGroupsLabel = (row: PickupLocationRow): string =>
  row.route?.groupNames.join(", ") || EMPTY_CELL;

export const trucksLabel = (row: PickupLocationRow): string =>
  row.route?.truckPlates.join(", ") || EMPTY_CELL;

export const customerLabel = (row: PickupLocationRow): string =>
  row.cg?.ownerOrganization?.name || EMPTY_CELL;

/** The container group's contract, as `contractFromLabel` writes it. */
export const contractLabel = (row: PickupLocationRow, t: TFn): string =>
  contractFromLabel(row.cg?.contract, t) || EMPTY_CELL;

export const contactLabel = (row: PickupLocationRow): string =>
  row.cg?.primaryContact?.name || EMPTY_CELL;

/** Empty rather than a dash: the cell renders a link only when there is a number. */
export const contactPhone = (row: PickupLocationRow): string => row.cg?.primaryContact?.phone ?? "";

export const pickupScheduleLabel = (row: PickupLocationRow, t: TFn): string => {
  if (!row.cg) return EMPTY_CELL;
  if (row.route && row.route.hasSchedule === undefined) return EMPTY_CELL;
  return row.route?.hasSchedule ? t(Namespace.Table, "yes") : t(Namespace.Table, "no");
};

const routeOf = (routeIndex: CgRouteIndex, cg: ConnectProto.ContainerGroup) =>
  routeIndex.byCg.get(String(cg.id)) ??
  (routeIndex.schedulesUnknown ? { ...emptyInfo(), hasSchedule: undefined } : undefined);

/** Interleaves each location's header row with its container groups. */
export const flattenLocations = (
  locations: ConnectProto.PickupLocation[],
  routeIndex: CgRouteIndex,
): PickupLocationRow[] =>
  locations.flatMap((location) => {
    const groups = location.containerGroups ?? [];
    const groupKey = locationGroupKey(location);
    return [
      { location, _isGroupHeader: true, _groupKey: groupKey },
      ...groups.map<PickupLocationRow>((cg, index) => ({
        location,
        cg,
        route: routeOf(routeIndex, cg),
        _groupKey: groupKey,
        _isLastInGroup: index === groups.length - 1,
      })),
    ];
  });

/**
 * Rows for the Excel export: one per container group, and a location without
 * any still contributes one bare row so it does not vanish from the report.
 */
export const flattenLocationsForExport = (
  locations: ConnectProto.PickupLocation[],
  routeIndex: CgRouteIndex,
): PickupLocationRow[] =>
  locations
    .flatMap<PickupLocationRow>((location) => {
      const groups = location.containerGroups ?? [];
      if (groups.length === 0) return [{ location }];
      return groups.map((cg) => ({
        location,
        cg,
        route: routeOf(routeIndex, cg),
      }));
    })
    .map((row, index) => ({ ...row, exportIndex: index + 1 }));
